import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetKnowledgeSubDomainsQuery } from "../../hooks/useGetKnowledgeSubDomainsQuery";
import { Key_knowledgeSubDomains } from "../../utils/constants";
import toast from "react-hot-toast";
import { routes } from "../../routes";
import { Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { KnowledgeSubDomainType } from "../../types/KnowledgeDomain";
import { updateKnowledgeSubDomain } from "../../services/school/knowledgedomains/knowledgeSubDomain";
import { setKnowledgeSubDomainData } from "../../redux/slices/school/knowledgeSubDomain";

const columnNamesMap: { [key: string]: string } = {
    description: "Nome",
    code: "Código",
};

export const KnowledgeSubDomains = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemToBeDeleted, setItemToBeDeleted] = useState<KnowledgeSubDomainType | null>(null);

    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();
    const { data, status } = useGetKnowledgeSubDomainsQuery({ PageNumber: currentPage, PageSize: 400 });

    const mutationUpdate = useMutation(updateKnowledgeSubDomain, {
        onSuccess: () => {
            queryClient.invalidateQueries(Key_knowledgeSubDomains);
            toast.success("Subdomínio de conhecimento removido");
        },
        onError: () => {
            toast.error("Erro ao apagar subdomínio de conhecimento.");
        }
    });

    const handleCreate = () => {
        navigate(routes.createKnowledgeSubDomain);
    };

    const handleEdit = (subDomain: KnowledgeSubDomainType) => {
        dispatch(setKnowledgeSubDomainData(subDomain));
        navigate(routes.updateKnowledgeSubDomain);
    };

    const handleDelete = (subDomain: KnowledgeSubDomainType) => {
        subDomain.status = false;
        mutationUpdate.mutateAsync(subDomain);
    };

    const openDeleteDialog = (subDomain: KnowledgeSubDomainType) => {
        setItemToBeDeleted(subDomain);
    };

    const confirmDelete = () => {
        if (itemToBeDeleted) {
            handleDelete(itemToBeDeleted);
        }
    };

    const options = data?.items.map((subDomain) => [
        {
            label: "Editar",
            onClick: () => handleEdit(subDomain),
            icon: <Pencil1Icon />
        },
        {
            label: "Excluir",
            onClick: () => openDeleteDialog(subDomain),
            icon: <TrashIcon />
        },
    ]) || [];

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    return (
        <>
            <Lists
                status={status}
                TableComponent={() => (
                    <TableComponent
                        columns={Object.keys(columnNamesMap)}
                        items={data?.items}
                        options={options}
                        columnNamesMap={columnNamesMap}
                        totalPages={data?.totalPages || 1}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                )}
                handleClickCreate={handleCreate}
            />
            <ConfirmAlertDialog
                open={!!itemToBeDeleted}
                onOpenChange={(open) => !open && setItemToBeDeleted(null)}
                title="Esta operação é irreversível"
                item={itemToBeDeleted}
                onConfirm={confirmDelete}
                onCancel={() => setItemToBeDeleted(null)}
            />
        </>
    );
};
